import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  BookOpen,
  Search,
  ExternalLink,
  Loader2,
  Wrench,
  Star,
  Bookmark,
  BookmarkCheck,
  Lock,
  Clock,
  Download,
} from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { getCrossLinks } from "@/lib/crossLinks";
import {
  isPlaybookSaved,
  togglePlaybookSaved,
  getRecentPlaybooks,
  markPlaybookViewed,
} from "@/lib/storage";
import { useUnlock } from "@/pages/Dashboard";
import { usePrice } from "@/lib/price";
import { trackEvent } from "@/lib/api";
import { requestLead } from "@/components/dashboard/LeadGateModal";

const WHOP = "https://whop.com/checkout/plan_hfsvR7lsOjQds";

function TierBadge({ tier }) {
  if (tier === "premium") {
    return (
      <span className="font-accent text-[9px] bg-[#FFC400] text-[#0a0a0a] border-2 border-[#0a0a0a] px-1.5 py-0.5 inline-flex items-center gap-1 leading-none">
        <Star size={9} strokeWidth={3} fill="#0a0a0a" /> PREMIUM
      </span>
    );
  }
  return (
    <span className="font-accent text-[9px] bg-[#FDFBF7] text-[#0a0a0a] border-2 border-[#0a0a0a] px-1.5 py-0.5 leading-none">
      FREE
    </span>
  );
}

function PlaybookCard({ p, toolCount, paid, openUnlock, isFocused, focusRef, recent }) {
  const [saved, setSavedState] = useState(isPlaybookSaved(p.slug));
  const [imgError, setImgError] = useState(false);
  const { price } = usePrice();
  const locked = p.tier === "premium" && !paid;

  const onSave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const next = togglePlaybookSaved(p);
    setSavedState(next);
  };

  const fullUrl = p.url?.startsWith("http") ? p.url : `https://jobhacki.com${p.url || ""}`;
  const pdfUrl = p.pdf || null;

  return (
    <div
      ref={isFocused ? focusRef : null}
      data-testid={`pb-card-${p.slug}`}
      data-tier={p.tier || "free"}
      className={`hover-pop block bg-white border-4 border-[#0a0a0a] shadow-brutal overflow-hidden flex flex-col ${
        isFocused ? "ring-4 ring-[#FFC400] ring-offset-2 ring-offset-[#FDFBF7]" : ""
      }`}
    >
      {p.image && !imgError && (
        <div className="aspect-[16/9] bg-[#FDFBF7] border-b-4 border-[#0a0a0a] overflow-hidden">
          <img
            src={p.image}
            alt=""
            loading="lazy"
            onError={() => setImgError(true)}
            className="w-full h-full object-cover"
          />
        </div>
      )}
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="font-accent text-[10px] text-[#E10600] truncate min-w-0">
            {p.category}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <TierBadge tier={p.tier} />
            {toolCount > 0 && (
              <Link
                to={`/dashboard/toolbox?via=${p.slug}`}
                data-testid={`pb-tools-${p.slug}`}
                className="font-accent text-[10px] bg-[#FFC400] border-2 border-[#0a0a0a] px-1.5 py-0.5 inline-flex items-center gap-1 leading-none hover:bg-[#0a0a0a] hover:text-[#FFC400]"
                title={`${toolCount} tools used in this playbook`}
              >
                <Wrench size={9} strokeWidth={3} /> {toolCount}
              </Link>
            )}
            <button
              onClick={onSave}
              data-testid={`pb-save-${p.slug}`}
              aria-label={saved ? "Unsave playbook" : "Save playbook"}
              className={`w-7 h-7 border-2 border-[#0a0a0a] inline-flex items-center justify-center ${
                saved ? "bg-[#FFC400]" : "bg-white"
              }`}
            >
              {saved ? <BookmarkCheck size={11} strokeWidth={3} /> : <Bookmark size={11} strokeWidth={3} />}
            </button>
          </div>
        </div>
        <div className="font-display text-base md:text-lg leading-tight">{p.title}</div>
        {p.keywords?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {p.keywords.slice(0, 4).map((k) => (
              <span
                key={k}
                className="font-mono-body text-[10px] bg-[#FDFBF7] border border-[#0a0a0a]/30 px-1.5 py-0.5"
              >
                {k}
              </span>
            ))}
          </div>
        )}
        <div className="mt-auto pt-4 flex flex-col gap-2">
          {recent && (
            <span
              data-testid={`pb-recent-${p.slug}`}
              className="font-mono-body text-[10px] text-[#0a0a0a]/55 inline-flex items-center gap-1 self-end"
            >
              <Clock size={9} strokeWidth={3} /> RECENT
            </span>
          )}
          {locked ? (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                openUnlock(p.title);
              }}
              data-testid={`pb-unlock-${p.slug}`}
              className="hover-pop inline-flex items-center justify-center gap-1.5 bg-[#0a0a0a] text-[#FFC400] border-2 border-[#0a0a0a] px-3 py-2 font-accent text-[11px] shadow-brutal-sm w-full"
            >
              <Lock size={11} strokeWidth={3} /> Unlock for {price}
            </button>
          ) : (
            <div className="flex gap-2">
              <a
                href={fullUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackEvent("playbook.open", { slug: p.slug });
                  markPlaybookViewed(p.slug);
                }}
                data-testid={`pb-read-${p.slug}`}
                className="hover-pop flex-1 inline-flex items-center justify-center gap-1.5 bg-[#0a0a0a] text-[#FFC400] border-2 border-[#0a0a0a] px-3 py-2 font-accent text-[11px] shadow-brutal-sm"
              >
                Read <ExternalLink size={11} strokeWidth={3} />
              </a>
              {pdfUrl && (
                <a
                  href={pdfUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={async (e) => {
                    e.preventDefault();
                    trackEvent("playbook.download", { slug: p.slug });
                    const gate = await requestLead({
                      source: "playbook",
                      label: `Download "${p.title}" PDF`,
                      meta: { playbook: p.slug },
                    });
                    if (!gate.ok) return;
                    markPlaybookViewed(p.slug);
                    // Trigger the actual download via a fresh anchor.
                    const a = document.createElement("a");
                    a.href = pdfUrl;
                    a.download = "";
                    a.rel = "noopener noreferrer";
                    a.target = "_blank";
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                  }}
                  data-testid={`pb-pdf-${p.slug}`}
                  aria-label={`Download ${p.title} PDF`}
                  title="Download PDF"
                  className="hover-pop inline-flex items-center justify-center gap-1.5 bg-[#FFC400] text-[#0a0a0a] border-2 border-[#0a0a0a] px-3 py-2 font-accent text-[11px] shadow-brutal-sm"
                >
                  <Download size={11} strokeWidth={3} /> PDF
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DashboardPlaybooks() {
  const [searchParams] = useSearchParams();
  const focusSlug = searchParams.get("focus") || "";
  const { paid } = useUnlock();
  const { price } = usePrice();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const focusRef = useRef(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const d = await getCrossLinks();
        if (cancel) return;
        if (!d.playbooks.length) {
          setError("Couldn't load the playbook feed. Try again in a moment.");
          setData(d);
        } else {
          setData(d);
        }
      } catch {
        if (!cancel) setError("Couldn't load the playbook feed.");
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  useEffect(() => {
    if (focusSlug && data && focusRef.current) {
      focusRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [focusSlug, data]);

  const playbooks = data?.playbooks || [];
  const playbookToTools = data?.playbookToTools;

  const cats = useMemo(() => {
    if (!playbooks.length) return [""];
    const set = new Set(playbooks.map((p) => p.category).filter(Boolean));
    return ["", ...Array.from(set).sort()];
  }, [playbooks]);

  // Order: PREMIUM tier first (gold), then recently-viewed, then feed order.
  const ordered = useMemo(() => {
    if (!playbooks.length) return [];
    const recent = getRecentPlaybooks();
    const slugIndex = new Map(recent.map((s, i) => [s, i]));
    return [...playbooks].sort((a, b) => {
      const ap = a.tier === "premium" ? 0 : 1;
      const bp = b.tier === "premium" ? 0 : 1;
      if (ap !== bp) return ap - bp;
      const ai = slugIndex.has(a.slug) ? slugIndex.get(a.slug) : Infinity;
      const bi = slugIndex.has(b.slug) ? slugIndex.get(b.slug) : Infinity;
      if (ai !== bi) return ai - bi;
      return 0;
    });
  }, [playbooks]);

  const list = useMemo(() => {
    return ordered.filter((p) => {
      if (cat && p.category !== cat) return false;
      if (!q) return true;
      const hay = `${p.title} ${p.category} ${(p.keywords || []).join(" ")}`.toLowerCase();
      return hay.includes(q.toLowerCase());
    });
  }, [ordered, q, cat]);

  const total = playbooks.length || null;
  const recentSet = useMemo(() => new Set(getRecentPlaybooks()), [playbooks]);

  return (
    <div data-testid="dash-playbooks" className="space-y-6">
      <div>
        <h1 className="font-display text-3xl md:text-4xl flex items-center gap-3">
          <BookOpen className="text-[#E10600]" /> PLAYBOOK DIRECTORY
        </h1>
        <p className="font-mono-body text-sm text-[#0a0a0a]/65 mt-1.5 max-w-xl">
          {total != null ? (
            <>
              <b data-testid="pb-total">{total}</b> grounded money &amp; career playbooks. Free ones
              open in a new tab. Premium are unlocked with All-Access ({price}).
            </>
          ) : (
            "Loading the live playbook feed…"
          )}
        </p>
      </div>

      <div className="grid grid-cols-12 gap-3">
        <div className="col-span-12 md:col-span-8 relative">
          <Search
            size={16}
            strokeWidth={2.5}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0a0a0a]/55"
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search playbooks…"
            data-testid="pb-search"
            className="w-full bg-white border-2 border-[#0a0a0a] pl-10 pr-4 py-3 font-mono-body text-[13px] shadow-brutal-sm focus:outline-none focus:bg-[#FDFBF7]"
          />
        </div>
        <div className="col-span-12 md:col-span-4">
          <select
            value={cat}
            onChange={(e) => setCat(e.target.value)}
            data-testid="pb-cat"
            className="w-full bg-white border-2 border-[#0a0a0a] px-3 py-3 font-mono-body text-[12px] shadow-brutal-sm"
          >
            {cats.map((c) => (
              <option key={c || "all"} value={c}>
                {c || "All categories"}
              </option>
            ))}
          </select>
        </div>
      </div>

      {data === null && (
        <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-10 text-center font-mono-body text-sm text-[#0a0a0a]/65">
          <Loader2 className="mx-auto animate-spin mb-3" /> Pulling 100+ playbooks…
        </div>
      )}

      {error && (
        <div className="bg-white border-4 border-[#0a0a0a] shadow-brutal p-6 font-mono-body text-sm text-[#E10600]">
          {error}
        </div>
      )}

      {playbooks.length > 0 && (
        <>
          <div className="font-mono-body text-[12px] text-[#0a0a0a]/65">
            Showing <b className="text-[#0a0a0a]">{list.length}</b> of {total}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((p) => (
              <PlaybookCard
                key={p.slug}
                p={p}
                toolCount={playbookToTools?.get(p.slug)?.length || 0}
                paid={paid}
                openUnlock={(t) => window.open(WHOP, "_blank", "noopener,noreferrer")}
                isFocused={p.slug === focusSlug}
                focusRef={focusRef}
                recent={recentSet.has(p.slug)}
              />
            ))}
            {list.length === 0 && (
              <div className="col-span-full font-mono-body text-sm text-[#0a0a0a]/65 text-center py-10">
                Nothing matches that search.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
